import React, { useCallback, useContext, useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { UserContext } from '../context/User';
import { API } from '../helpers';
import {
  buildConsoleRailLinks,
  isConsoleNavActive,
} from '../helpers/consoleRailLinks';

function sessionRoleSummary(user) {
  if (!user || typeof user.role !== 'number') return null;
  if (user.role >= 100) {
    return {
      label: 'ROOT',
      title:
        'ROOT — full console, Users, Redemptions, Logs, and Settings (system / operation / other).',
    };
  }
  if (user.role >= 10) {
    return {
      label: 'ADMIN',
      title:
        'ADMIN — Users, Redemptions, plus standard console; no system-wide Settings tabs.',
    };
  }
  return {
    label: 'MEMBER',
    title:
      'MEMBER — Tokens, Top-up, Overview, Logs; channel management is admin-only.',
  };
}

const GlobalHomeLink = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [userState, userDispatch] = useContext(UserContext);

  const handleLogout = useCallback(async () => {
    try {
      await API.get('/api/user/logout');
    } catch {
      // 仍清除本地会话，避免卡在已失效 cookie
    }
    userDispatch({ type: 'logout' });
    localStorage.removeItem('user');
    navigate('/login');
  }, [navigate, userDispatch]);

  const roleSummary = useMemo(
    () => sessionRoleSummary(userState.user),
    [userState.user]
  );

  const consoleLinks = useMemo(
    () => buildConsoleRailLinks(t, !!userState.user),
    [userState.user, t]
  );

  if (location.pathname === '/') return null;

  return (
    <div className='global-app-top-wrap'>
      <nav
        className='global-console-subnav'
        aria-label={userState.user ? 'Console shortcuts' : 'Site shortcuts'}
      >
        {consoleLinks.map((item) => {
          const active = isConsoleNavActive(location.pathname, item.to);
          return (
            <Link
              key={item.key}
              to={item.to}
              className={
                active
                  ? 'global-console-subnav-link is-active'
                  : 'global-console-subnav-link'
              }
            >
              {item.label}
            </Link>
          );
        })}
      </nav>
      <div className='global-home-link-bar' role='navigation' aria-label='Account shortcuts'>
        <Link to='/' className='global-home-link' aria-label='Back to home'>
          {'< HOME'}
        </Link>
        {userState.user && roleSummary && (
          <span
            className='global-home-session'
            title={roleSummary.title}
            aria-label={`Signed in as ${userState.user.username}, ${roleSummary.label}`}
          >
            @{userState.user.username}
            <span className='global-home-session-sep'> · </span>
            <span className='global-home-session-role'>{roleSummary.label}</span>
          </span>
        )}
        {userState.user && (
          <>
            <Link to='/setting' className='global-home-link'>
              ACCOUNT
            </Link>
            <button type='button' className='global-home-link' onClick={handleLogout}>
              LOGOUT
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default GlobalHomeLink;
